import { useState, useEffect } from 'react';
import * as THREE from 'three';
import './styles.css';

export const SceneSlicer = ({ three }) => {
  const { camera, gl, scene, raycaster , controlRef } = three;
  const [enabled, setEnabled] = useState(false);
  const [axis, setAxis] = useState('y');
  const [width, setWidth] = useState(10);
  const [center, setCenter] = useState(1120);
  const [constraintCamera, setConstraintCamera] = useState(false);
  const [lookDirection, setLookDirection] = useState(null);

  const axisLimits = {
    x: { min: 200000, max: 210000 },
    y: { min: 200, max: 1300 },
    z: { min: 7970000, max: 7980000 },
  };

  const [zones] = useState({
    ConcessionShaft: 204450,
    SouthwellShaft: 205409,
    BurnettShaft: 205563,
    E1:205374,
    E2:205410,
    W1:205320
    // add more zones here...
  });
  const [levels] = useState({
    level2:1120,
    level3:1086,
    level4:1055,
    level5:1013,
    level6:934,
    level7:888,
    level8:847,
    level9:801,
    level10:754,
    level11:667,
    level12:595,
    level13:522,
    level14:445,
    level15:375,
    level16:305
  });
  const [zzones] = useState({
    ConcessionShaft: 7975265,
    SouthwellShaft: 7975163,
    BurnettShaft: 7975112,
  });

  // Set up clipping planes and plane helpers
  useEffect(() => {
    gl.localClippingEnabled = enabled;

    if (!enabled) {
      gl.clippingPlanes = [];
      return;
    }

    const planes = [];
    const halfWidth = width / 2;

    let center_ = center;

    // Create clipping planes based on the selected axis and center
    if (axis === 'x') {
      center_ = center - zones.E1;  // Adjust for zone
      planes.push(new THREE.Plane(new THREE.Vector3(1, 0, 0), -(center_ - halfWidth)));
      planes.push(new THREE.Plane(new THREE.Vector3(-1, 0, 0), (center_ + halfWidth)));
    } else if (axis === 'y') {
      center_ = center - 1120;  // Adjust for y-axis
      planes.push(new THREE.Plane(new THREE.Vector3(0, 1, 0), -(center_ - halfWidth)));
      planes.push(new THREE.Plane(new THREE.Vector3(0, -1, 0), center_ + halfWidth));
    } else if (axis === 'z') {
      center_ = zzones.Z1 - center;  // Adjust for zzones
      planes.push(new THREE.Plane(new THREE.Vector3(0, 0, 1), -(center_ - halfWidth)));
      planes.push(new THREE.Plane(new THREE.Vector3(0, 0, -1), center_ + halfWidth));
    }

    gl.clippingPlanes = planes;

    return () => {
      gl.clippingPlanes = [];
    };
  }, [enabled, axis, width, center, gl, scene, zones, zzones]);

  const clipValue = (value, axis) => {
    const { min, max } = axisLimits[axis];
    if (value > max) {
      return max;
    }
    if (value < min) {
      return min;
    }
    return value;
  };

  const handleAxisChange = (e) => {
    const selectedAxis = e.target.value;
    setAxis(selectedAxis);
    setCenter(clipValue(center, selectedAxis)); // Clip the center value according to the new axis
  };

  const handleZoneChange = (e) => {
    const selectedZone = e.target.value;
    const newCenter = zones[selectedZone];
    setCenter(clipValue(newCenter, axis)); // Clip the new center value
  };

  const handleLevelChange = (e) => {
    const selectedLevel = e.target.value;
    const newCenter = levels[selectedLevel];
    setCenter(clipValue(newCenter, axis)); // Clip the new center value
  };

  const handleZZoneChange = (e) => {
    const selectedZZone = e.target.value;
    const newCenter = zzones[selectedZZone];
    setCenter(clipValue(newCenter, axis)); // Clip the new center value
  };

  const handleCenterChange = (e) => {
    const newCenter = parseFloat(e.target.value) || 0;
    setCenter(newCenter); // Allow free editing, do not clip yet
  };

  const handleCenterBlur = () => {
    setCenter(clipValue(center, axis)); // Apply clipping when the input loses focus
  };

  // Update OrbitControls based on constraintCamera
  useEffect(() => {

    console.log(lookDirection)

    const controls =  controlRef.current
    // Only proceed if controls exist
    if (!controls) return;
    
    // Disable rotation when constraintCamera is enabled
    if (constraintCamera) {
      controls.enableRotate = false;
    } else {
      controls.enableRotate = true;
    }

    // Handle lookDirection based on axis and constraintCamera
    if (lookDirection) {
      const direction = new THREE.Vector3();
      if (axis === 'x') {
        if (lookDirection === 'north') direction.set(0, 0, -1);
        else if (lookDirection === 'south') direction.set(0, 0, 1);
      } else if (axis === 'y') {
        if (lookDirection === 'up') direction.set(0, 1, 0);
        else if (lookDirection === 'down') direction.set(0, -1, 0);
      } else if (axis === 'z') {
        if (lookDirection === 'east') direction.set(1, 0, 0);
        else if (lookDirection === 'west') direction.set(-1, 0, 0);
      }

      camera.lookAt(direction);
    }
  }, [constraintCamera, lookDirection]);

  return (
    <>
      {enabled && (
        <div className={`slicerRoot ${enabled ? 'slicer-expanded' : ''}`}>
          <div className="slicer-controls">
            <div className="control-group">
              <label>Axis:</label>
              <select value={axis} onChange={handleAxisChange}>
                <option value="x">X</option>
                <option value="y">Z</option>
                <option value="z">Y</option>
              </select>
            </div>

            <div className="control-group">
              <label>Width:</label>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={width}
                onChange={(e) => setWidth(Math.max(0.1, parseFloat(e.target.value) || 1))}
              />
            </div>

            <div className="control-group">
              <label>Center:</label>
              <input
                type="number"
                step="0.1"
                value={center}
                onChange={handleCenterChange}
                onBlur={handleCenterBlur} // Apply clipping when focus is lost
              />
            </div>

            {axis === 'x' && (
              <div className="control-group">
                <label>Zone:</label>
                <select onChange={handleZoneChange}>
                  {Object.keys(zones).map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {axis === 'y' && (
              <div className="control-group">
                <label>Level:</label>
                <select onChange={handleLevelChange}>
                  {Object.keys(levels).map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {axis === 'z' && (
              <div className="control-group">
                <label>Z Zone:</label>
                <select onChange={handleZZoneChange}>
                  {Object.keys(zzones).map((zzone) => (
                    <option key={zzone} value={zzone}>
                      {zzone}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <hr className="slicer-separator" />
        </div>
      )}

      <div className="slicer-toggle-container">
        <button
          className={`slicer-toggle ${enabled ? 'active' : ''}`}
          onClick={() => setEnabled(!enabled)}
        >
          ✂️
        </button>
      </div>
    </>
  );
};
